import React, {useEffect} from 'react';
import {Organization, ICertificate} from '@app/models';
import {useTypedTranslation} from '@app/hooks';
import {Box, FormGroup, Typography} from '@material-ui/core';
import {PasslyButton} from '@app/components/Buttons';
import {Autorenew} from '@material-ui/icons';
import {localStore} from '@app/helpers/domStorage.helper';
import {useSelector} from 'react-redux';
import {defaultOrgId} from '@app/helpers/constants';
import {red} from '@material-ui/core/colors';
import {theme} from '@app/helpers';

interface IProps {
    connected: boolean;
    onDisconnect: () => void;
    domain?: string;
}

export const ManualGraphConfig = ({connected, onDisconnect, domain}: IProps) => {
    const {translation} = useTypedTranslation();

    const organizations: Organization[] = useSelector((state: any) => state.basic.shellConfig.organizations);
    const homeRealmSuffix: string = useSelector((state: any) => state.basic.shellConfig.configs.homeRealmSuffix);
    const appCertificate: ICertificate = useSelector((state: any) => state.appLibrary.appCertificate);

    useEffect(() => {
        if (appCertificate === undefined || appCertificate.CerEncoded === undefined || appCertificate.CerEncoded === '') {
            return;
        }

        const spaces = `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`;

        let certSplit = appCertificate.RawData.match(/.{1,76}/g);

        let certEncoded = '';

        if (certSplit !== null) {
            certSplit.forEach(function (line, index) {
                if (index + 1 < certSplit!.length) {
                    certEncoded += '"' + line + '" +<br />' + spaces;
                } else {
                    certEncoded += '"' + line + '"';
                }
            });
        }

        let certElement = document.querySelector('#cert');
        certElement!.innerHTML = certEncoded;
    }, [appCertificate]);

    const generateHomeRealm = () => {
        const orgId = localStore.getItem('org_filter') || defaultOrgId;

        let hr = '';

        organizations.forEach(function (o) {
            if (o.id === orgId) {
                hr = o.homeRealm;
            }
        });

        return `${hr}.${homeRealmSuffix}`;
    };

    return (
        <>
            {/* CONNECTED */}
            {connected && (
                <Box pb={1}>
                    <Typography variant='h3'>{translation('MSOL_DISCONNECT_HEADER')}</Typography>
                    <div>
                        <div className='danger'>
                            <b>{translation('MSOL_WARNING_LABEL')}</b>:&nbsp;
                            <span>
                                {translation('GRAPH_DISCONNECT_WARNING')}
                            </span>
                        </div>
                        <PasslyButton
                            variant='contained'
                            style={{backgroundColor: red[400], color: theme.palette.primary.contrastText}}
                            onClick={onDisconnect}
                            startIcon={<Autorenew/>}
                        >
                            {translation('MSOL_DISCONNECT')}
                        </PasslyButton>
                    </div>
                </Box>
            )}

            <Typography variant='h3'>{translation('MSOL_CONFIGURE_MANUAL_HEADER')}</Typography>
            <FormGroup>
                <div>
                    <Typography variant='h3'>{translation('MSOL_PREREQUISITES')}</Typography>
                    <ul>
                        <li>
                            PowerShell 7 and later is the recommended PowerShell version for use with the Microsoft
                            Graph PowerShell SDK on all platforms. There are no additional prerequisites to use the SDK
                            with PowerShell 7 or later.
                        </li>
                    </ul>

                    <Typography variant='h3'>{translation('GRAPH_PS_PREREQUISITES')}</Typography>
                    <ul>
                        <li>
                            <a href='https://learn.microsoft.com/en-us/powershell/scripting/windows-powershell/install/installing-windows-powershell?view=powershell-7.3#upgrading-existing-windows-powershell'
                               target='_blank' rel='noopener noreferrer'>
                                {translation('PS_UPGRADE')}
                            </a>
                        </li>
                        <li>
                            <a href='https://learn.microsoft.com/en-us/dotnet/framework/install/' target='_blank' rel='noopener noreferrer'>
                                {translation('DOTNET472_INSTALLER')}
                            </a>
                        </li>
                        <li>
                            {translation('PSGET_UPDATE')}
                            <blockquote>
                                <code>
                                    Install-Module PowerShellGet -Force
                                </code>
                            </blockquote>
                        </li>
                    </ul>
                    <Typography variant='h3'>{translation('GRAPH_PS_INSTALL')}</Typography>
                    <ol>
                        <li>
                            <span>{translation('GRAPH_PS_INSTALL_STEP1')}</span>
                            <blockquote>
                                <code>
                                    Install-Module Microsoft.Graph -Scope CurrentUser<br/>
                                    Import-Module Microsoft.Graph.Identity.DirectoryManagement
                                </code>
                            </blockquote>
                        </li>
                    </ol>

                    <Typography variant='h3'>{translation('MSOL_MANUAL_POWERSHELL_HEADER')}</Typography>
                    <ol>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP1')}</span><br/>
                            <blockquote>
                                <code>
                                    Connect-MgGraph -Scopes "Domain.ReadWrite.All, Directory.AccessAsUser.All"
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP2')}</span>
                        </li>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP3')}</span>
                            <blockquote>
                                <code>
                                    $domain = "<span>{domain}</span>"<br/>
                                    $params = @{'{'}<br/>
                                    "@odata.type" = "#microsoft.graph.internalDomainFederation"<br/>
                                    DisplayName = "Passly"<br/>
                                    IssuerUri = "https://<span>{domain}.{generateHomeRealm()}</span>/trust"<br/>
                                    MetadataExchangeUri = "https://<span>{generateHomeRealm()}</span>/services/trust/mex"<br/>
                                    PassiveSignInUri = "https://<span>{generateHomeRealm()}</span>/trust/launch"<br/>
                                    PreferredAuthenticationProtocol = "wsFed"<br/>
                                    ActiveSignInUri = "https://<span>{generateHomeRealm()}</span>/services/trust/2005/mixed"<br/>
                                    SignOutUri = "https://<span>{generateHomeRealm()}</span>/trust/launch"<br/>
                                    IsSignedAuthenticationRequestRequired = $true<br/>
                                    FederatedIdpMfaBehavior = "acceptIfMfaDoneByFederatedIdp"<br/>
                                    SigningCertificate = <span id='cert'/><br/>
                                    {'}'}<br/>
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP4')}</span>
                            <blockquote>
                                <code>
                                    New-MgDomainFederationConfiguration -DomainId $domain -BodyParameter $params
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP5')}</span><br/>
                            <blockquote>
                                <code>
                                    $internalDomainFederationId = (Get-MgDomainFederationConfiguration -DomainId $domain).Id<br/>
                                    Update-MgDomainFederationConfiguration -DomainId $domain -InternalDomainFederationId $internalDomainFederationId -BodyParameter $params
                                </code>
                            </blockquote>
                        </li>
                        <li>
                            <span>{translation('GRAPH_MANUAL_STEP6')}</span><br/>
                            <blockquote>
                                <code>
                                    Get-MgDomainFederationConfiguration -DomainId $domain -All | Format-List
                                </code>
                            </blockquote>
                        </li>
                    </ol>
                </div>
            </FormGroup>
        </>
    );
};